import ErrorNote from './ErrorNote'
import { useCan, useSession } from '../useSession'
import { canOpenRoster } from '../api/calendar'
import { bookingIsOff, lessonSessionOf, withoutShownPrivates } from '../api/privateLessons'
﻿import { useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { useCalendarFilter, withFilter } from '../useCalendarFilter'
import CalendarFilters from './CalendarFilters'
import { type CalendarView } from './CalendarViewSwitcher'
import ScheduleHeader from './ScheduleHeader'
import CalendarLink from './CalendarLink'
import RosterModal from './RosterModal'
import BookingForm from './BookingForm'
import CloseTheStudioSheet from './CloseTheStudioSheet'
import { fetchClosureProgress, reopenTheStudio } from '../api/closures'
import {
  assignColumns,
  buildRoomItems,
  minutesOf,
  snapToSlot,
  stretchGrid,
  fetchBookings,
  type BookedSlot,
  bookingMatches,
  type RoomItem,
} from '../api/bookings'
import {
  addDays,
  closureHeadline,
  fetchDay,
  fetchFilterOptions,
  formatFullDate,
  formatTime,
  meridiem,
  today,
  toMinutes,
  type CalendarDayDetail,
} from '../api/calendar'

const ROW_HEIGHT = 22 // px per 15-minute slot
const FALLBACK_COLOUR = 'var(--color-ink-3)'

/** Just the parts of a room column the booking form needs. */
type DayRoomLike = { roomId: string; name: string; color: string | null }

const bookingsByRoom = (bookings: BookedSlot[], roomId: string) =>
  bookings.filter((b) => b.roomId === roomId)

// The bookings the grid draws: `buildRoomItems` leaves a cancelled one out, so a
// cancelled private keeps its struck-through session instead (D-733).
const drawnBookings = (bookings: BookedSlot[]) => bookings.filter((b) => !bookingIsOff(b))

/**
 * One class or booking, positioned over the slots it occupies.
 *
 * Both kinds share a block so the grid reads as "what is in this room" rather
 * than as two overlaid schedules. A booking is distinguished by weight, not by
 * position: dashed when nobody is in it yet, and italic detail text.
 */
function RoomBlock({
  item,
  gridStartMinutes,
  slotMinutes,
  colour,
  onOpen,
}: {
  item: RoomItem & { column: number; columnCount: number }
  gridStartMinutes: number
  slotMinutes: number
  colour: string
  onOpen: (kind: 'class' | 'booking', id: string) => void
}) {
  const start = toMinutes(item.startTime)
  const end = toMinutes(item.endTime)

  const rowStart = Math.floor((start - gridStartMinutes) / slotMinutes) + 1
  const rowEnd = Math.max(rowStart + 1, Math.ceil((end - gridStartMinutes) / slotMinutes) + 1)
  const slots = rowEnd - rowStart
  const width = 100 / item.columnCount

  /*
   * **A block only offers a roster the server would give** (D-539). A plain
   * dancer holds no `ViewPeople`, and a dancer on the staff may read only the
   * classes she is on - so for either, her own Tuesday ballet used to be a
   * button that answered with a permission box or `Roster request failed (404)`.
   * Bookings are untouched: they never had a roster to refuse.
   */
  const { session } = useSession()
  const mayOpen = item.kind === 'booking' || canOpenRoster(item.instructorIds, session ?? {})

  return (
    <button
      type='button'
      disabled={!mayOpen}
      aria-disabled={!mayOpen}
      onClick={() => { if (mayOpen) onOpen(item.kind, item.sourceId) }}
      title={`${item.label} - ${formatTime(item.startTime)}${meridiem(item.startTime)} to ${formatTime(item.endTime)}${meridiem(item.endTime)}`}
      className="room-tint absolute overflow-hidden rounded-md px-1.5 py-1 text-left transition-[filter] hover:brightness-110 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
      style={
        {
          top: `${(rowStart - 1) * ROW_HEIGHT}px`,
          height: `${slots * ROW_HEIGHT - 2}px`,
          left: `calc(${item.column * width}% + 1px)`,
          width: `calc(${width}% - 2px)`,
          '--room-color': colour,

          // Opaque, not translucent. The 15-minute rules mean "this slot is
          // free to book", so letting them show through an occupied block
          // muddles the one signal the grid exists to give.
          background: item.isOpen ? 'transparent' : 'var(--room-fill)',
          borderLeft: '3px solid var(--room-display)',
          border: item.isOpen ? '1px dashed var(--room-display)' : undefined,
          opacity: item.isCancelled ? 0.55 : 1,
        } as React.CSSProperties
      }
    >
      <div className="tabular text-[10.5px] font-semibold" style={{ color: 'var(--room-display)' }}>
        {formatTime(item.startTime)}
        {meridiem(item.startTime)}
        {`-${formatTime(item.endTime)}${meridiem(item.endTime)}`}
      </div>

      <div
        className={`truncate text-[11.5px] leading-tight font-semibold ${
          item.isCancelled ? 'text-ink-3 line-through' : 'text-ink'
        }`}
      >
        {item.label}
      </div>

      {slots >= 3 && item.detail && (
        <div
          className={`truncate text-[10.5px] text-ink-3 ${item.kind === 'booking' ? 'italic' : ''}`}
        >
          {item.detail}
        </div>
      )}
    </button>
  )
}

function DayGrid({
  day,
  bookings,
  fullDay,
  onOpen,
  onNewBooking,
  canBook,
}: {
  day: CalendarDayDetail
  bookings: BookedSlot[]
  fullDay: boolean
  onOpen: (kind: 'class' | 'booking', id: string) => void
  onNewBooking: (room: DayRoomLike, startMinutes: number) => void
  /** She may book a room, so an empty slot is an invitation (D-318, D-539). */
  canBook: boolean
}) {
  // Widened for anything booked outside class hours, or a morning private
  // renders at a negative row and silently does not appear (D-79).
  /*
   * Class hours by default, the whole bookable window on demand (D-79).
   * Privates run outside the evening timetable, so those hours have to be
   * reachable — but rendering fourteen hours on an ordinary class night is
   * mostly white space, and the evening is what the grid is read for.
   */
  const { start: gridStartMinutes, end: gridEndMinutes } = fullDay
    ? { start: toMinutes(day.bookingWindowStart), end: toMinutes(day.bookingWindowEnd) }
    : stretchGrid(toMinutes(day.gridStart), toMinutes(day.gridEnd), bookings)
  const slotCount = Math.max(1, (gridEndMinutes - gridStartMinutes) / day.slotMinutes)

  const slots = Array.from({ length: slotCount }, (_, i) => gridStartMinutes + i * day.slotMinutes)

  return (
    <div className="overflow-x-auto rounded-xl border border-line bg-surface">
      <div
        className="grid min-w-[820px]"
        style={{ gridTemplateColumns: `62px repeat(${day.rooms.length}, minmax(120px, 1fr))` }}
      >
        {/* Header row */}
        <div className="sticky left-0 z-10 border-b border-line bg-surface-2" />
        {day.rooms.map((room) => (
          <div
            key={room.roomId}
            className="room-tint border-b border-l border-line bg-surface-2 px-2 py-2 text-center"
            style={
              {
                '--room-color': room.color ?? FALLBACK_COLOUR,
                borderTop: '2px solid var(--room-display)',
              } as React.CSSProperties
            }
          >
            <div className="text-[12px] font-semibold">{room.name}</div>
            <div className="text-[10.5px] text-ink-3">
              {(() => {
                // Counts both, since a room busy with privates is not free.
                const used =
                  withoutShownPrivates(room.entries, drawnBookings(bookings)).length + bookingsByRoom(bookings, room.roomId).length
                return used === 0 ? 'Free all day' : `${used} booked`
              })()}
            </div>
          </div>
        ))}

        {/* Time gutter. Every slot is a row; only the hour gets a label. */}
        <div
          className="sticky left-0 z-10 grid bg-surface-2"
          style={{ gridTemplateRows: `repeat(${slotCount}, ${ROW_HEIGHT}px)` }}
        >
          {slots.map((minutes) => {
            const isHour = minutes % 60 === 0
            const h = Math.floor(minutes / 60)
            const label = `${h % 12 === 0 ? 12 : h % 12}${h >= 12 ? 'pm' : 'am'}`

            return (
              <div
                key={minutes}
                className={`tabular pr-2 text-right text-[10px] ${
                  isHour ? 'font-semibold text-ink-2' : 'text-ink-3'
                }`}
                style={{ borderTop: `1px solid var(--color-${isHour ? 'line-2' : 'line'})` }}
              >
                {isHour ? label : ''}
              </div>
            )
          })}
        </div>

        {/*
          One column per room. The slot rules sit in a background layer so class
          blocks can span rows freely on top of them: those 15-minute lines are
          what makes the grid bookable in Phase 3.
        */}
        {day.rooms.map((room) => (
          <div
            key={room.roomId}
            /*
              `cursor-copy` was the only hint that this is bookable, and a
              cursor does not exist on a phone (D-318). The title says it for a
              mouse; the button above the grid says it for everyone else.
            */
            title={canBook ? `Tap an empty slot to book ${room.name}` : undefined}
            className={`relative border-l border-line ${canBook ? 'cursor-copy' : ''}`}
            onClick={(e) => {
              /*
               * Only the background counts. A click that landed on a block
               * belongs to that block, which opens the roster.
               *
               * The blocks sit in a full-height container covering the column,
               * so most background clicks land on that rather than on the
               * column itself — both are marked, and anything else is a block.
               */
              // An audit found this ungated: a dancer routed here by D-539
              // tapped a slot, filled the form, and POST /api/bookings refused.
              if (!canBook) return

              const target = e.target as HTMLElement;
              if (target !== e.currentTarget && target.dataset.slotLayer === undefined) return

              const box = e.currentTarget.getBoundingClientRect()
              const offset = e.clientY - box.top
              const minutes = gridStartMinutes + (offset / ROW_HEIGHT) * day.slotMinutes
              onNewBooking(room, snapToSlot(minutes, day.slotMinutes))
            }}
          >
            <div
              data-slot-layer="true"
              className="pointer-events-none absolute inset-0 grid"
              style={{ gridTemplateRows: `repeat(${slotCount}, ${ROW_HEIGHT}px)` }}
            >
              {slots.map((minutes) => (
                <div
                  key={minutes}
                  style={{
                    borderTop: `1px solid var(--color-${minutes % 60 === 0 ? 'line-2' : 'line'})`,
                  }}
                />
              ))}
            </div>

            <div
              data-slot-layer="true"
              className="relative"
              style={{ height: slotCount * ROW_HEIGHT }}
            >
              {assignColumns(
                buildRoomItems(withoutShownPrivates(room.entries, drawnBookings(bookings)), bookingsByRoom(bookings, room.roomId)),
              ).map((item) => (
                <RoomBlock
                  key={item.id}
                  item={item}
                  gridStartMinutes={gridStartMinutes}
                  slotMinutes={day.slotMinutes}
                  colour={room.color ?? FALLBACK_COLOUR}
                  onOpen={onOpen}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function CalendarDayView({
  date: target,
  onDate: goDay,
  onView,
  actions,
  teachingOnly = false,
}: {
  /**
   * Show only what she teaches, leaving her own children's classes out
   * (D-374). Set by `TeacherSchedule` for *My classes*; off everywhere else,
   * which is the union home needs.
   */
  teachingOnly?: boolean
  /**
   * The day on screen, owned by the caller (D-360).
   *
   * It used to read the route parameter itself and report a date change through
   * `onView('day', date)` — one callback carrying two different events. Now the
   * caller says where the day lives and `onView` means only what it says.
   */
  date: string
  onDate: (date: string) => void
  /**
   * What a view change does. **Required** (D-395) — D-312 made it optional, and
   * the absent case routed to `/calendar/*` from inside the switcher, so an
   * embed that forgot it offered a parent controls landing on "Not your page".
   * The route wrappers navigate now, as they already do for `onDate`.
   */
  onView: (view: CalendarView, date: string) => void
  /**
   * Buttons that belong with the date controls rather than above them (D-321).
   *
   * "Book a room" started life as its own row over the calendar and read as a
   * banner. It is the same kind of thing as Week/Day/Month — something you do
   * to this view — so it sits in the same line, to the left of the switcher.
   *
   * **Passing any means you bring *Book a room* too** (D-717): the day's own
   * button steps aside rather than appear twice.
   */
  actions?: ReactNode
}) {
  const [urlFilter, setFilter] = useCalendarFilter()
  /*
   * Which half of her schedule, folded into the filter so the react-query key
   * moves with it (D-374). Set by `TeacherSchedule` for *My classes*; off
   * everywhere else, which is the union.
   */
  const filter = teachingOnly ? { ...urlFilter, teachingOnly: true } : urlFilter

  const [searchParams] = useSearchParams()
  const [roster, setRoster] = useState<{ kind: 'class' | 'booking'; id: string } | null>(null)
  const [newBooking, setNewBooking] = useState<{
    room: DayRoomLike
    startMinutes: number
    // False from the toolbar button, which has no slot to name (D-428).
    chosen: boolean
  } | null>(null)
  const [editing, setEditing] = useState<BookedSlot | null>(null)
  const { session: viewer } = useSession()
  const [fullDay, setFullDay] = useState(false)
  const client = useQueryClient()

  /*
   * Decided once, here, and handed to the grid (D-539). This file had no
   * capability check at all: it was safe only because both callers were
   * teachers or admins, and D-539 routes a dancer holding `ViewSchedule` alone
   * into it. Every booking affordance below reads this one answer.
   */
  const canBook = useCan('BookRooms')

  /*
   * <b>Only Tonya, and only forward</b> (D-735). The capability is the whole
   * of who may (A-3), and the endpoint refuses a past day anyway - this stops
   * the control appearing on a day where pressing it can only produce a
   * refusal, which reads as the app being broken rather than as the rule it is.
   */
  const mayCloseTheStudio = useCan('CloseStudio')
  const [closing, setClosing] = useState(false)

  // The server honours ?dancer= only for this capability (D-606), so the chip
  // must ask the same question or it can claim a filter nobody applied.
  const mayFilterByDancer = useCan('ViewAllSchedules')

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['calendar', 'day', target, filter],
    queryFn: () => fetchDay(target, filter),
    placeholderData: (previous) => previous,
  })

  const { data: options } = useQuery({
    queryKey: ['calendar', 'filters', target, filter.wholeStudio, filter.teachingOnly],
    queryFn: () => fetchFilterOptions(target, { wholeStudio: filter.wholeStudio, teachingOnly: filter.teachingOnly }),
  })

  // Privates are roughly half of what occupies a room (D-78), so a room grid
  // without them answers "is this room free?" wrongly most evenings.
  const { data: bookings } = useQuery({
    queryKey: ['bookings', target],
    queryFn: () => fetchBookings(target, target),
  })

  /*
   * <b>The chips narrow these too</b> (D-770, extended here by an audit).
   *
   * The week carries its filter into this screen (`withFilter` on every day
   * link), and the classes here are already narrowed by it - `fetchDay` takes
   * the filter. So the privates being exempt made the reported bug reappear
   * one tap later: filter to one teacher on the week, open a day, and every
   * private in the building is back beside a chip that says otherwise.
   *
   * <b>The same predicate the week uses</b>, because this is one question
   * asked on two screens and the answer must not depend on which one you are
   * looking at (A-17) - `useCalendarFilter` already carries the scar of two
   * serialisers that drifted (D-368).
   */
  const shown = (bookings ?? []).filter((b) => bookingMatches(b, filter))

  return (
    <div className="page-shell px-5 py-8">
      <ScheduleHeader
        label={formatFullDate(target)}
        anchor={target}
        view="day"
        onPick={goDay}
        onView={(view) => onView(view, target)}
        onStep={(d) => goDay(addDays(target, d))}
        onToday={() => goDay(today())}
        actions={
          <>
            {/*
              **The only way to book a room that does not need a pointer**
              (D-427).

              Tapping an empty slot on the grid is the fast path and stays the
              fast path — but it is a `div` with an `onClick`, so it has no
              role, no name and no tab stop. Nothing keyboard-driven could open
              this form at all, and a screen reader was told nothing existed.
              The comment on that div has said "the button above the grid says
              it for everyone else" since D-318; the slot for it was here and
              the day route never filled it.

              **One button, not one per slot.** A button per empty slot would
              be honest and unusable: fifty-odd slots across six rooms is three
              hundred tab stops before the calendar itself. This opens the form
              at the start of the bookable window and lets her change the room
              and time in it — which is now the first thing she meets there
              (D-426).

              **Only where the caller brings no controls of its own** (D-717).
              The teacher's schedule passes `actions` with its own *Book a room*
              - the one it shows on the week and the month too - so adding this
              beside it put two identical buttons on her day.
            */}
            {canBook && !actions && data && data.rooms.length > 0 && (
              <button
                type="button"
                onClick={() =>
                  setNewBooking({
                    room: data.rooms[0],
                    startMinutes: minutesOf(data.bookingWindowStart),
                    chosen: false,
                  })
                }
                className="min-h-[36px] rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3"
              >
                Book a room
              </button>
            )}

            {/*
              <b>With the other things you do to this day, not floating over the
              grid</b> (D-781). It sat below the filters and above the rooms,
              which put the one irreversible control on the screen in the one
              place nothing else was - so it read as part of the calendar rather
              than as an action, and the owner's first words on seeing it in QA
              were that the location is really bad. *Book a room* learned the
              same lesson in D-321.

              <b>Outlined in warn rather than filled</b>: it belongs beside
              *Book a room*, and a solid destructive button in a toolbar is a
              thing people mis-tap.
            */}
            {mayCloseTheStudio && data && !data.isClosed && target >= today() && (
              <button
                type="button"
                onClick={() => setClosing(true)}
                className="min-h-[36px] rounded-lg border border-warn px-3 py-1.5 text-[12.5px] font-semibold text-warn hover:bg-warn-bg"
              >
                Close the studio
              </button>
            )}
            {actions}
          </>
        }
        hideTeachers={Boolean(onView)}
        above={
          <CalendarLink
            to={withFilter(`/calendar/week/${target}`, searchParams)}
            onActivate={onView ? () => onView('week', target) : undefined}
            // `py-1`: measured 106x18, and the day is where Tonya now lands
            // (D-599), so this is the way out of her first screen.
            className="mb-1 block py-1 text-[12px] font-semibold text-accent no-underline hover:underline"
          >
            &larr; Back to the week
          </CalendarLink>
        }
        subtitle={
          data && (
            <p className="mt-1 text-sm text-ink-3">
              {data.entries.length} {data.entries.length === 1 ? 'session' : 'sessions'} &middot;{' '}
              {data.slotMinutes}-minute slots
            </p>
          )
        }
      />

      {/*
        <b>Whose evening this is, and the way out of it</b> (D-606).

        The dancer filter arrives from a conflict rather than from a control,
        so nothing on this screen would otherwise say why the day is nearly
        empty — a filter with no visible cause reads as a studio with no
        classes, which is D-209's failure in a different place.
      */}
      {/*
        <b>Gated on the same capability the server gates the filter on.</b>
        This component is also embedded in the teacher portal, which is not
        behind `ViewAllSchedules` — so a hand-typed `?dancer=` there would have
        drawn "Showing Ellie Cotton's evening" over an ordinary day the server
        had not filtered at all. A chip that states a filter nobody applied is
        worse than no chip.
      */}
      {filter.dancer && mayFilterByDancer && (
        <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-accent bg-surface-2 px-4 py-2.5 text-[12.5px]">
          <span className="min-w-0 flex-1">
            {/* `||`, not `??`: an empty parameter is as nameless as a missing one. */}
            Showing <strong>{filter.dancerName || 'one dancer'}</strong>&rsquo;s evening.
          </span>

          <button
            type="button"
            onClick={() => setFilter({ ...filter, dancer: undefined, dancerName: undefined })}
            className="flex min-h-[2.25rem] shrink-0 items-center gap-1.5 rounded-lg border border-accent px-2.5 py-1.5 text-[12px] font-semibold text-accent-2"
          >
            Show the whole day
          </button>
        </div>
      )}

      <CalendarFilters
        options={options}
        filter={filter}
        onChange={setFilter}
        matched={data?.entries.length ?? 0}
        total={data?.entries.length ?? 0}
      />

      {isError && (
        <ErrorNote boxed>
          {(error as Error).message}
        </ErrorNote>
      )}

      {/*
        Privates may be booked well outside class hours (D-79), so those hours
        have to be reachable — there is nothing to click on a grid that does not
        render them. Off by default, because the evening is what the grid is
        read for and fourteen hours of it would be mostly empty.
      */}
      {data && (
        <div className="mb-3 flex items-center gap-2">
          <button
            onClick={() => setFullDay((v) => !v)}
            aria-pressed={fullDay}
            className={`rounded-lg border px-3 py-1.5 text-[13px] font-semibold transition-colors ${
              fullDay
                ? 'border-accent bg-accent-soft text-accent-2'
                : 'border-line-2 bg-surface text-ink-2 hover:border-ink-3'
            }`}
          >
            {fullDay ? 'Class hours' : 'Whole day'}
          </button>

          <span className="text-[12.5px] text-ink-3">
            {fullDay
              ? `Showing ${formatTime(data.bookingWindowStart)}${meridiem(data.bookingWindowStart)}–${formatTime(data.bookingWindowEnd)}${meridiem(data.bookingWindowEnd)}, the whole bookable day`
              : 'Switch to the whole day to book outside class hours'}
          </span>
        </div>
      )}

      {isPending && <p className="text-sm text-ink-3">Loading...</p>}

      {data?.isClosed && (
        <div className="mb-4 rounded-xl border border-warn bg-warn-bg px-4 py-3 text-sm text-warn">
          {/*
            ⚠ This read "Studio closed" whatever the closure's scope was, because
            the day was never given `regularScheduleOnly` (D-868, D-870) - so on a
            festival day it announced a shut studio directly above the privates
            and one-off classes that were going ahead.
          */}
          <b>{closureHeadline(data.closedFrom, data.regularScheduleOnly)}.</b>{' '}
          {data.closureReason}
          {mayCloseTheStudio && <ClosureProgressLine date={target} />}

          {/*
            <b>The other half of the decision, on the banner announcing it</b>
            (D-738, D-792). Reopening had a tested endpoint and no control at
            all — a capability nobody could reach, which is the same fault as a
            link that refuses the person holding it, arriving from the other
            side. Found by the owner on QA asking how to undo one.
          */}
          {mayCloseTheStudio && <PutTheDayBack date={target} />}
        </div>
      )}

      {/*
        The control itself lives up with *Book a room* (D-781). It is not
        offered on a day that is already shut: tightening is a real operation
        (D-741) but it belongs with reopening, which is the other half of
        changing a closure and has to tell people. Offering "close" on a closed
        day would read as a way to change it and refuse when she tried.
      */}
      {closing && (
        <CloseTheStudioSheet
          date={target}
          onDismiss={() => setClosing(false)}
          onClosed={() => {
            setClosing(false)
            // The day, the week and the month all change, and so does the
            // progress that is about to start reporting.
            client.invalidateQueries({ queryKey: ['calendar'] })
            client.invalidateQueries({ queryKey: ['closures'] })
          }}
        />
      )}

      {data && (
        <DayGrid
          day={data}
          bookings={shown}
          fullDay={fullDay}
          onOpen={(kind, id) => {
            // A booking is ours to change; a class is Jackrabbit's, so it only
            // offers its roster.
            if (kind === 'booking') {
              const slot = shown.find((b) => b.id === id)
              // A private's block is the lesson: it opens her attendance (D-733).
              const lesson = slot ? lessonSessionOf(slot) : null
              if (slot && lesson && canOpenRoster([slot.staffId], viewer ?? {})) {
                setRoster({ kind: 'class', id: lesson })
                return
              }
              if (slot) { setEditing(slot); return }
            }
            setRoster({ kind, id })
          }}
          onNewBooking={(room, startMinutes) => setNewBooking({ room, startMinutes, chosen: true })}
          canBook={canBook}
        />
      )}

      {roster && (
        <RosterModal
          kind={roster.kind}
          id={roster.id}
          onClose={() => setRoster(null)}
          onOpenSession={(id) => setRoster({ kind: 'class', id })}
          onEditBooking={
            canBook
              ? (bookingId) => {
                  const slot = shown.find((b) => b.id === bookingId)
                  if (!slot) return
                  setRoster(null)
                  setEditing(slot)
                }
              : undefined
          }
        />
      )}

      {editing && (
        <BookingForm
          roomId={editing.roomId}
          date={target}
          startMinutes={0}
          existing={editing}
          onClose={() => setEditing(null)}
        />
      )}

      {newBooking && (
        <BookingForm
          roomId={newBooking.room.roomId}
          date={target}
          startMinutes={newBooking.startMinutes}
          startChosen={newBooking.chosen}
          onClose={() => setNewBooking(null)}
        />
      )}

      {data && data.unscheduled.length > 0 && (
        <section className="mt-5 rounded-xl border border-line bg-surface p-4">
          <h2 className="mb-2 text-[12px] font-bold tracking-[0.11em] text-ink-3 uppercase">
            Not yet scheduled
          </h2>
          <p className="mb-3 text-[12.5px] text-ink-3">
            These classes exist but have no day, time, or room assigned yet.
          </p>
          <ul className="flex flex-col gap-1.5">
            {data.unscheduled.map((entry) => (
              <li key={entry.occurrenceId} className="text-[13px] wrap-anywhere text-ink-2">
                {entry.className}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

/**
 * How far the telling got, under the closed banner (D-736).
 *
 * **Only for somebody who may close the studio.** It is a count of who the
 * studio reached and failed to reach, which is hers rather than a teacher's -
 * and the endpoint refuses anybody else, so rendering it would produce a
 * permanent error on a banner everyone sees.
 *
 * **Three different nothings, said three different ways** (D-209). A request
 * that failed, a closure nothing is telling anybody about, and a send that has
 * not started are distinct states, and the one thing none of them may render as
 * is *Told 0 people* - which reads as a working send that reached nobody.
 */
/**
 * Putting a closed day back on (D-738, D-792).
 *
 * <b>Asks first, because it reaches everybody.</b> Reopening tells the people
 * the closure told — a second message about a night that turned out to be
 * normal — so it is not a thing to do by brushing past a button. The confirm
 * names what it will put back, since a range is one decision (D-740) and the
 * day on screen may be one of several.
 *
 * <b>A refusal is a sentence, not a status code.</b> It is refused once the
 * first class it called off would have started, and on a day that has been and
 * gone; both are correct and neither is obvious, so the screen says which.
 */
function PutTheDayBack({ date }: { date: string }) {
  const client = useQueryClient()
  const [asked, setAsked] = useState(false)

  const reopen = useMutation({
    mutationFn: () => reopenTheStudio(date),
    onSuccess: () => {
      setAsked(false)
      client.invalidateQueries({ queryKey: ['calendar'] })
      client.invalidateQueries({ queryKey: ['closures'] })
    },
  })

  if (!asked) {
    return (
      <div className="mt-2">
        <button
          type="button"
          onClick={() => setAsked(true)}
          className="rounded-lg border border-warn px-2.5 py-1 text-[12.5px] font-semibold text-warn"
        >
          Put the day back on
        </button>

        {reopen.isError && (
          <p role="alert" className="mt-1.5 text-[12.5px] font-normal">
            {(reopen.error as Error).message}
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <span className="text-[12.5px] font-normal">
        This puts back everything the closure called off, and tells the people it told.
      </span>

      <button
        type="button"
        disabled={reopen.isPending}
        onClick={() => reopen.mutate()}
        className="rounded-lg border border-warn bg-warn-bg px-2.5 py-1 text-[12.5px] font-bold text-warn disabled:opacity-60"
      >
        {reopen.isPending ? 'Putting it back...' : 'Yes, reopen'}
      </button>

      <button
        type="button"
        onClick={() => setAsked(false)}
        className="px-1.5 py-1 text-[12.5px] font-semibold"
      >
        Cancel
      </button>
    </div>
  )
}

function ClosureProgressLine({ date }: { date: string }) {
  const { data, isPending, isError } = useQuery({
    queryKey: ['closures', 'progress', date],
    queryFn: () => fetchClosureProgress(date),
    /*
     * While it is still going, ask again. Two hundred people at the provider's
     * pace is minutes, and a number frozen at "38 so far" reads as a send that
     * has stalled.
     */
    refetchInterval: (query) => (query.state.data && !query.state.data.finished ? 3000 : false),
    retry: false,
  })

  if (isPending) return null

  if (isError) {
    return <p className="mt-1 text-xs">We could not check who has been told.</p>
  }

  // No send at all: nothing is telling anybody, which on a host without the
  // sender switched on is the ordinary state and worth saying out loud.
  if (!data) return <p className="mt-1 text-xs">Nobody has been told.</p>

  if (data.expected === null) {
    return <p className="mt-1 text-xs">Working out who to tell...</p>
  }

  const settled = data.told + data.notTold + data.notKnown

  return (
    <p className="mt-1 text-xs">
      {data.finished
        ? `Told ${data.told} of ${data.expected}.`
        : `Telling ${data.expected} people: ${settled} so far.`}
      {data.notTold > 0 && ` ${data.notTold} could not be told.`}
      {data.notKnown > 0 && ` ${data.notKnown} we are not sure about.`}
      {data.unreachable !== null && data.unreachable > 0 &&
        ` ${data.unreachable} we hold no address or login for.`}
    </p>
  )
}
